import { normalizePath, useSetFlowTo } from 'Simple/Flow'
import { useMutation } from 'Data/Api'
import { useDataValue, useDataSubmit, useDataChange } from 'Simple/Data'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import mutationCreateAutomations from './mutation-create-automations.graphql.js'
import mutationUpdatePreferences from './mutation-update-preferences.graphql.js'

// TODO: this likely needs to be mapped to the new type/fields because
// I think it references an app automation's event not the actual event?
function validate(value) {
  return value
    .filter(step => step.step_type === 'automation')
    .map(({ event }) => {
      if (!event?.data) {
        return null
      }
      switch (event.type) {
        case 'txs_status_update': {
          if (!event.data.value_to?.length) {
            return 'Please select treatment status'
          }

          break
        }
        case 'appointment_bookings_state_update': {
          if (
            !event.data.value_to?.length ||
            !event.data.appointment_type_ids?.length
          ) {
            return 'Please select appointment types and status'
          }

          break
        }
        case 'custom_resource_insert': {
          if (!event.data.custom_resource_type) {
            return 'Please select the custom resource type'
          }
          break
        }

        default:
          return null
      }

      return null
    })
    .filter(Boolean)
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location._id',
  })
  let connection_title = useDataValue({
    viewPath: props.viewPath,
    context: 'app',
    path: 'name',
  })
  let app_id = useDataValue({
    viewPath: props.viewPath,
    context: 'app',
    path: 'id',
  })
  let submitConnection = useDataSubmit({
    context: 'connection',
    viewPath: props.viewPath,
  })

  let connection = useDataValue({
    context: 'connection',
    viewPath: props.viewPath,
  })

  let setRefetchAutomations = useDataChange({
    context: 'tab',
    path: 'selected.refetch_automations',
    viewPath: props.viewPath,
  })

  let onConnectionResourceAdd = useOnConnectionResourceAdd(props)

  let [, executeCreateAutomationsMutation] = useMutation(
    mutationCreateAutomations
  )
  let [, executeUpdatePreferencesMutation] = useMutation(
    mutationUpdatePreferences
  )
  let setFlow = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let connection_id = useDataValue({
    viewPath: props.viewPath,
    context: 'connection',
    path: 'id',
  })

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args.type) {
      case 'confirm': {
        // https://linear.app/greyfinch/issue/ALI-307/cannot-enable-the-same-connection-for-two-or-more-locations
        // If the location is not referenced in the connection_resources table,
        // it must be added to the database to prevent property configuration errors
        await onConnectionResourceAdd({ value })
        return
      }
      case 'changeStatus': {
        let isLast = value.current_index === value.steps.length - 1

        if (isLast) {
          await submit(
            value.steps.map((step, index) => {
              if (index === value.current_index) {
                return {
                  ...step,
                  status: args.value,
                }
              }

              return step
            })
          )

          setRefetchAutomations(Date.now())
          setFlow(normalizePath(props.viewPath, '../../../SetupAutomations/No'))
        } else {
          await savePreferences(value.steps)
          change(next => {
            if (next.steps[next.current_index]) {
              next.steps[next.current_index].status = args.value
              next.current_index = next.current_index + 1
            }
          })
        }

        return
      }
      default: {
        await submit(
          value.steps.map((step, index) => {
            if (index === value.current_index) {
              return {
                ...step,
                status: 'completed',
              }
            }

            return step
          })
        )
        setRefetchAutomations(Date.now())
        setFlow(normalizePath(props.viewPath, '../../../SetupAutomations/No'))

        return
      }
    }
  }

  async function savePreferences(steps) {
    let preferences_step = steps.find(step => step.step_type === 'preferences')

    if (preferences_step) {
      let properties = Object.fromEntries(
        steps
          .filter(step => step.step_type === 'preferences' && step.value)
          .map(step => [step.key, step.value])
      )
      let connection_id = preferences_step.connection_id

      let mutationResponse = await executeUpdatePreferencesMutation({
        properties,
        connection_id,
        resource_id: preferences_step.location_id,
      })
      if (mutationResponse.error) {
        notify(
          notifyError(
            'Something went wrong. Please, try again or contact support if the problem persists.'
          )
        )

        return
      }
    }
  }

  async function submit(steps) {
    let connection_resource_status =
      connection?.current_location_resource?.status ?? 'created'

    let errorMessages = validate(steps)

    if (errorMessages?.length) {
      return notify(notifyError(errorMessages.join('\n')))
    }

    await savePreferences(steps)

    let automationSteps =
      steps.filter(
        item => item.step_type === 'automation' && item.status === 'completed'
      ) || []

    if (automationSteps.length > 0) {
      let mutationResponse = await executeCreateAutomationsMutation({
        location_id,
        events: automationSteps.map(
          ({ event, automation_id, required, name }) => ({
            ...event,
            name,
            automation_id,
            required,
          })
        ),
        app_id,
      })

      if (mutationResponse.error) {
        notify(
          notifyError(
            'Something went wrong. Please, try again or contact support if the problem persists.'
          )
        )

        return
      } else {
        if (connection_resource_status !== 'enabled') {
          notify(
            notifySuccess(`Connection to ${connection_title} is ready to use.`)
          )
        }
      }
    }

    if (connection_resource_status === 'enabled') {
      submitConnection({ type: 'refetch' })
    } else {
      submitConnection({
        type: 'activate_location_connection',
        connection_id,
      })
    }
  }
}

function useOnConnectionResourceAdd(props) {
  let [, executeUpdatePreferencesMutation] = useMutation(
    mutationUpdatePreferences
  )
  let setFlow = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let connection_id = useDataValue({
    viewPath: props.viewPath,
    context: 'connection',
    path: 'id',
  })

  let connection_resources = useDataValue({
    viewPath: props.viewPath,
    context: 'connection',
    path: 'resources',
  })

  let location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location._id',
  })

  return async function onAddConnectionResource({ value }) {
    if (
      connection_resources.every(
        resource => resource.resource_id !== location_id
      )
    ) {
      let mutationResponse = await executeUpdatePreferencesMutation({
        properties: {},
        connection_id,
        resource_id: location_id,
      })
      if (mutationResponse.error) {
        notify(
          notifyError(
            'Something went wrong. Please, try again or contact support if the problem persists.'
          )
        )
        return
      }
    }

    if (value.steps.length > 0) {
      setFlow(normalizePath(props.viewPath, 'Content/Steps/Content'))
    }
  }
}
